import { Fragment, type NodeSpec, Slice } from '@atlaskit/editor-prosemirror/model';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';

import type {
	StreamingCommandFactory,
	SetAttrsCommand,
} from '../aiStreamingOrchestratorPluginType';
import { findNodeByLocalId } from './utils/find-node-by-local-id';

const getValidAttributes = (attrs: Record<string, unknown>, nodeSpec: NodeSpec) => {
	const validAttrs: Record<string, unknown> = {};
	for (const key in attrs) {
		if (nodeSpec.attrs?.hasOwnProperty(key)) {
			validAttrs[key] = attrs[key];
		}
	}
	return validAttrs;
};

export const createSetAttrsCommand: StreamingCommandFactory<SetAttrsCommand> =
	(api) => (command) => {
		return api.core.actions.execute(({ tr }) => {
			let shouldExecute = false;
			findNodeByLocalId(api, tr, command.localId, ({ node, pos }) => {
				if (node) {
					const attrs = command.attrs;
					if (!attrs || typeof attrs !== 'object') {
						return;
					}
					const validAttrs = getValidAttributes(attrs, node.type.spec);
					node.type.spec.attrs;
					api.aiExperience?.actions.tagAsAIStreamingTransformation(tr, { isFinal: true });

					// [CCI-17297] Capture the node BEFORE the attr change as the
					// segment's original content; setAttrs is non-streamable so we
					// record the reviewable "update" segment ourselves.
					const originalSlice = new Slice(Fragment.from(node), 0, 0);
					const from = tr.mapping.map(pos);

					tr.setNodeMarkup(pos, node.type, {
						...node.attrs,
						...validAttrs,
					});
					tr.setSelection(TextSelection.near(tr.doc.resolve(pos)));

					// Capture the node AFTER the attr change as the new content and
					// record a coarse "update" segment spanning it. No-op when the
					// New AIFC experience is OFF.
					const mappedFrom = tr.mapping.map(from);
					const updatedNode = tr.doc.nodeAt(mappedFrom);
					if (updatedNode) {
						api.aiExperience?.actions.recordAIContentSegments(tr, [
							{
								startPos: mappedFrom,
								endPos: mappedFrom + updatedNode.nodeSize,
								originalUserContentSlice: originalSlice,
								newSlice: new Slice(Fragment.from(updatedNode), 0, 0),
								kind: 'update',
							},
						]);
					}

					shouldExecute = true;
				}
			});
			if (shouldExecute) {
				api.aiExperience?.actions.stampAgentEditChrome(tr);
			}
			return shouldExecute ? tr : null;
		});
	};
